import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { devWarn } from "../utils/logger";

const API = process.env.REACT_APP_BACKEND_URL;

// Enable sending cookies with every request (httpOnly cookie auth)
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isRefreshing = useRef(false);
  const failedQueue = useRef([]);

  const processQueue = useCallback((err, token = null) => {
    failedQueue.current.forEach(prom => {
      if (err) {
        prom.reject(err);
      } else {
        prom.resolve(token);
      }
    });
    failedQueue.current = [];
  }, []);

  const doLogout = useCallback(async () => {
    try {
      await axios.post(`${API}/api/auth/logout`);
    } catch (err) { /* proceed with local cleanup even if logout API fails */ devWarn("Logout API failed (proceeding with local cleanup):", err); }
    localStorage.removeItem('tds_user');
    setUser(null);
  }, []);

  const doRefreshToken = useCallback(async () => {
    try {
      // Cookies are sent automatically via withCredentials.
      // Send empty body; backend reads refresh_token from cookie.
      const response = await axios.post(`${API}/api/auth/refresh`, {
        refresh_token: ''
      });
      return response.data.access_token;
    } catch (err) {
      doLogout();
      throw err;
    }
  }, [doLogout]);

  // Axios interceptor for automatic token refresh on 401
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (err) => {
        const originalRequest = err.config;

        if (err.response?.status === 401 && !originalRequest._retry) {
          // Don't try to refresh for auth endpoints - just reject
          if (originalRequest.url?.includes('/auth/refresh') || 
              originalRequest.url?.includes('/auth/me') ||
              originalRequest.url?.includes('/auth/login') ||
              originalRequest.url?.includes('/auth/logout')) {
            return Promise.reject(err);
          }

          if (isRefreshing.current) {
            return new Promise((resolve, reject) => {
              failedQueue.current.push({ resolve, reject });
            }).then(() => axios(originalRequest))
              .catch(e => Promise.reject(e));
          }

          originalRequest._retry = true;
          isRefreshing.current = true;

          try {
            await doRefreshToken();
            processQueue(null);
            return axios(originalRequest);
          } catch (refreshError) {
            processQueue(refreshError, null);
            return Promise.reject(refreshError);
          } finally {
            isRefreshing.current = false;
          }
        }

        return Promise.reject(err);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [doLogout, doRefreshToken, processQueue]);

  // Initialize auth state on mount by calling /api/auth/me
  useEffect(() => {
    const initAuth = async () => {
      // Try to restore user from localStorage first for instant UI
      const savedUser = localStorage.getItem('tds_user');
      if (savedUser) {
        try {
          setUser(JSON.parse(savedUser));
        } catch (_) {
          localStorage.removeItem('tds_user');
        }
      }

      // Fetch fresh user data from server (cookies sent automatically)
      try {
        const response = await axios.get(`${API}/api/auth/me`);
        const freshUser = response.data;
        localStorage.setItem('tds_user', JSON.stringify(freshUser));
        setUser(freshUser);
      } catch (_) {
        // Not authenticated or token expired - clear local state
        localStorage.removeItem('tds_user');
        setUser(null);
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const register = useCallback(async (email, password, fullName) => {
    try {
      setError(null);
      const response = await axios.post(`${API}/api/auth/register`, {
        email,
        password,
        full_name: fullName,
        role: 'customer'
      });

      const { user: userData } = response.data;
      localStorage.setItem('tds_user', JSON.stringify(userData));
      setUser(userData);
      return userData;
    } catch (err) {
      const message = err.response?.data?.detail || 'Registration failed';
      setError(message);
      throw new Error(message);
    }
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      setError(null);
      const response = await axios.post(`${API}/api/auth/login`, {
        email,
        password
      });

      const { user: userData } = response.data;
      localStorage.setItem('tds_user', JSON.stringify(userData));
      setUser(userData);
      return userData;
    } catch (err) {
      const message = err.response?.data?.detail || 'Login failed';
      setError(message);
      throw new Error(message);
    }
  }, []);

  const logout = useCallback(() => {
    doLogout();
  }, [doLogout]);

  const refreshToken = useCallback(async () => {
    return doRefreshToken();
  }, [doRefreshToken]);

  // Role check helpers - grouped into a single memo derived from user
  const roleHelpers = useMemo(() => {
    const role = user?.role;
    return {
      isAdmin: () => role === 'admin',
      isEmployee: () => role === 'employee',
      isCustomer: () => role === 'customer',
      canEdit: () => role === 'admin' || role === 'employee',
      canViewOnly: () => role === 'customer',
      hasClientAccess: (clientId) => {
        if (!user) return false;
        if (role === 'admin') return true;
        return user.assigned_client_ids?.includes(clientId) || false;
      },
    };
  }, [user]);

  // Auth actions - stable across re-renders (all callbacks are memoised)
  const authActions = useMemo(() => ({
    register, login, logout, refreshToken,
  }), [register, login, logout, refreshToken]);

  const contextValue = useMemo(() => ({
    user,
    loading,
    error,
    isAuthenticated: !!user,
    ...authActions,
    ...roleHelpers,
  }), [user, loading, error, authActions, roleHelpers]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
